// src/utils/colorUtils.js
// Centralized color utility functions for Divine Frameworks

/**
 * Calculate the relative luminance of a hex color (WCAG standard)
 * @param {string} hexColor - Hex color string (e.g., "#7C3AED")
 * @returns {number} Luminance value between 0 (black) and 1 (white)
 */
export const getLuminance = (hexColor) => {
  try {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16) / 255;
    const g = parseInt(hex.substr(2, 2), 16) / 255;
    const b = parseInt(hex.substr(4, 2), 16) / 255;

    const adjustedR = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
    const adjustedG = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
    const adjustedB = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

    return 0.2126 * adjustedR + 0.7152 * adjustedG + 0.0722 * adjustedB;
  } catch {
    return 0.5; // Default to mid-range
  }
};

/**
 * Get appropriate text color (black or white) for a background color (WCAG AA compliant)
 * @param {string} backgroundColor - Hex background color
 * @returns {string} "#1F2937" (dark) or "#FFFFFF" (white)
 */
export const getTextColor = (backgroundColor) => {
  try {
    const luminance = getLuminance(backgroundColor);
    return luminance > 0.5 ? '#1F2937' : '#FFFFFF';
  } catch {
    return '#1F2937';
  }
};

/**
 * Get appropriate secondary text color (gray) for a background color
 * @param {string} backgroundColor - Hex background color
 * @returns {string} Gray color hex
 */
export const getSecondaryTextColor = (backgroundColor) => {
  try {
    const luminance = getLuminance(backgroundColor);
    return luminance > 0.5 ? '#6B7280' : '#D1D5DB';
  } catch {
    return '#6B7280';
  }
};

/**
 * Convert hex color to HSL
 * @param {string} hex - Hex color string
 * @returns {object} { h: number (0-360), s: number (0-1), l: number (0-1) }
 */
export const hexToHsl = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { h: 0, s: 0, l: 0 };

  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s;
  const l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // Achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }

  return { h: h * 360, s, l };
};

/**
 * Convert HSL to hex color
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-1)
 * @param {number} l - Lightness (0-1)
 * @returns {string} Hex color string (uppercase)
 */
export const hslToHex = (h, s, l) => {
  // Normalize values
  h = ((h % 360) + 360) % 360;
  s = Math.max(0, Math.min(1, s));
  l = Math.max(0, Math.min(1, l));

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;

  let r, g, b;
  if (h < 60) { r = c; g = x; b = 0; }
  else if (h < 120) { r = x; g = c; b = 0; }
  else if (h < 180) { r = 0; g = c; b = x; }
  else if (h < 240) { r = 0; g = x; b = c; }
  else if (h < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }

  const toHex = (n) => {
    const hex = Math.round((n + m) * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
};

/**
 * Get template-specific default background color
 * @param {string} templateId - 'minimal' | 'warm' | 'bold'
 * @returns {string} Hex background color
 */
export const getTemplateBackground = (templateId) => {
  switch (templateId) {
    case 'bold': return '#1F2937';
    case 'warm': return '#FAF8F5';
    case 'minimal':
    default: return '#FFFFFF';
  }
};

/**
 * Generate color theory palette options based on a primary color
 * @param {string} primaryHex - Starting hex color
 * @param {string} selectedTemplate - Template ID for background color
 * @returns {Array} Array of palette options with names, descriptions, and colors
 */
export const generateColorTheoryPalettes = (primaryHex, selectedTemplate) => {
  const primary = hexToHsl(primaryHex);
  const background = getTemplateBackground(selectedTemplate);
  const isDark = selectedTemplate === 'bold';

  // Adjust primary lightness for dark backgrounds
  const adjustedPrimary = isDark
    ? hslToHex(primary.h, Math.min(primary.s + 0.1, 1), Math.max(primary.l, 0.55))
    : primaryHex;

  return [
    {
      id: 'complementary',
      name: 'Complementary',
      description: 'High contrast, energetic',
      explanation: 'Colors opposite each other on the color wheel create vibrant, eye-catching combinations.',
      colors: {
        primary: adjustedPrimary,
        secondary: hslToHex((primary.h + 180) % 360, Math.min(primary.s, 0.7), isDark ? 0.6 : 0.5),
        background,
        accent: hslToHex((primary.h + 210) % 360, Math.min(primary.s, 0.6), isDark ? 0.65 : 0.55),
      },
    },
    {
      id: 'analogous',
      name: 'Analogous',
      description: 'Harmonious, calming',
      explanation: 'Colors next to each other on the wheel create a soothing, cohesive feel.',
      colors: {
        primary: adjustedPrimary,
        secondary: hslToHex((primary.h + 30) % 360, Math.min(primary.s, 0.65), isDark ? 0.6 : 0.55),
        background,
        accent: hslToHex((primary.h - 30) % 360, Math.min(primary.s, 0.6), isDark ? 0.65 : 0.6),
      },
    },
    {
      id: 'triadic',
      name: 'Triadic',
      description: 'Balanced, dynamic',
      explanation: 'Three colors equally spaced on the wheel — balanced yet visually interesting.',
      colors: {
        primary: adjustedPrimary,
        secondary: hslToHex((primary.h + 120) % 360, Math.min(primary.s, 0.6), isDark ? 0.55 : 0.5),
        background,
        accent: hslToHex((primary.h + 240) % 360, Math.min(primary.s, 0.6), isDark ? 0.6 : 0.55),
      },
    },
    {
      id: 'monochromatic',
      name: 'Monochromatic',
      description: 'Elegant, unified',
      explanation: 'Variations of the same color — sophisticated and easy on the eyes.',
      colors: {
        primary: adjustedPrimary,
        secondary: hslToHex(primary.h, Math.max(primary.s - 0.2, 0.2), isDark ? 0.7 : 0.65),
        background,
        accent: hslToHex(primary.h, Math.max(primary.s - 0.3, 0.15), isDark ? 0.75 : 0.7),
      },
    },
    {
      id: 'split-complementary',
      name: 'Split-Complementary',
      description: 'Vibrant but softer',
      explanation: 'Like complementary, but uses the two colors adjacent to the opposite — less tension, more nuance.',
      colors: {
        primary: adjustedPrimary,
        secondary: hslToHex((primary.h + 150) % 360, Math.min(primary.s, 0.6), isDark ? 0.6 : 0.5),
        background,
        accent: hslToHex((primary.h + 210) % 360, Math.min(primary.s, 0.6), isDark ? 0.65 : 0.55),
      },
    },
  ];
};

/**
 * Validate if a string is a valid hex color
 * @param {string} hex - Color string to validate
 * @returns {boolean} True if valid hex color
 */
export const isValidHex = (hex) => {
  return /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex);
};

/**
 * Ensure hex color has # prefix
 * @param {string} hex - Color string
 * @returns {string} Hex with # prefix
 */
export const normalizeHex = (hex) => {
  if (!hex) return '#000000';
  return hex.startsWith('#') ? hex : `#${hex}`;
};

/**
 * Convert hex color to rgba with opacity
 * @param {string} hex - Hex color string
 * @param {number} opacity - Opacity value (0-1)
 * @returns {string} rgba color string
 */
export const hexToRgba = (hex, opacity = 1) => {
  try {
    const normalized = normalizeHex(hex).replace('#', '');
    const r = parseInt(normalized.substr(0, 2), 16);
    const g = parseInt(normalized.substr(2, 2), 16);
    const b = parseInt(normalized.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  } catch {
    return `rgba(124, 58, 237, ${opacity})`; // Fallback to primary purple
  }
};

/**
 * Adjust brightness of a hex color
 * @param {string} hex - Hex color string
 * @param {number} amount - Amount to adjust (-1 to 1, positive = lighter, negative = darker)
 * @returns {string} Adjusted hex color
 */
export const adjustBrightness = (hex, amount) => {
  try {
    const hsl = hexToHsl(hex);
    const newL = Math.max(0, Math.min(1, hsl.l + amount));
    return hslToHex(hsl.h, hsl.s, newL);
  } catch {
    return hex;
  }
};

/**
 * Darken a color by a percentage
 * @param {string} hex - Hex color string
 * @param {number} percent - Percentage to darken (0-100)
 * @returns {string} Darkened hex color
 */
export const darkenColor = (hex, percent) => {
  try {
    const hsl = hexToHsl(hex);
    const factor = 1 - (percent / 100);
    const newL = hsl.l * factor;
    return hslToHex(hsl.h, hsl.s, newL);
  } catch {
    return hex;
  }
};

/**
 * Get alternating background color (3% darker or lighter)
 * @param {string} bgColor - Base background hex color
 * @returns {string} Alternating background hex color
 */
export const getAltBackground = (bgColor) => {
  try {
    const luminance = getLuminance(bgColor);
    // For light backgrounds (luminance > 0.5), go 3% darker
    // For dark backgrounds, go 3% lighter
    if (luminance > 0.5) {
      return adjustBrightness(bgColor, -0.03);
    } else {
      return adjustBrightness(bgColor, 0.03);
    }
  } catch {
    return bgColor;
  }
};

/**
 * Calculate always-dark footer background based on site background
 * @param {string} siteBackground - Site's main background hex color
 * @returns {string} Footer background hex color (always dark)
 */
export const getFooterBackground = (siteBackground) => {
  try {
    const luminance = getLuminance(siteBackground);

    // Very light sites (luminance > 0.7): Use #1F2937 (gray-800)
    if (luminance > 0.7) {
      return '#1F2937';
    }

    // Medium sites (luminance > 0.4): Use #111827 (gray-900)
    if (luminance > 0.4) {
      return '#111827';
    }

    // Dark sites: Darken by 50% or use #0A0A0A minimum
    const darkened = darkenColor(siteBackground, 50);
    const darkenedLuminance = getLuminance(darkened);

    // Ensure footer is dark enough (luminance < 0.1)
    if (darkenedLuminance < 0.1) {
      return darkened;
    } else {
      return '#0A0A0A';
    }
  } catch {
    return '#1F2937'; // Fallback to gray-800
  }
};
